
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PlusCircle, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { UseFormReturn } from "react-hook-form";
import { LensFormValues } from "@/types/inventory";
import { LensTabsForm } from "./LensTabsForm";
import { SortButton, SortDirection, SortOption } from "./SortButton";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";

interface LensSearchAndAddProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isAddDialogOpen: boolean;
  setIsAddDialogOpen: (isOpen: boolean) => void;
  form: UseFormReturn<LensFormValues>;
  onSubmit: (values: LensFormValues) => Promise<void>;
  activeSort: string | null;
  sortDirection: SortDirection;
  handleSortChange: (value: string) => void;
  handleDirectionChange: () => void;
  sortOptions: SortOption[];
}

export function LensSearchAndAdd({
  searchQuery,
  setSearchQuery,
  isAddDialogOpen,
  setIsAddDialogOpen,
  form,
  onSubmit,
  activeSort,
  sortDirection,
  handleSortChange,
  handleDirectionChange,
  sortOptions,
}: LensSearchAndAddProps) {
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  return (
    <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-2">
      <div className={`flex ${isMobile ? 'flex-col gap-3' : 'flex-row items-center gap-4'} w-full`}>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('search_lenses')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        
        <div className={`flex ${isMobile ? 'flex-row justify-between items-center' : 'flex-row items-center'} gap-3`}>
          <SortButton 
            options={sortOptions}
            activeSort={activeSort}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            onDirectionChange={handleDirectionChange}
          />
          
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className={`whitespace-nowrap ${isMobile ? 'h-9' : ''}`}>
                <PlusCircle className="h-4 w-4 mr-1 sm:mr-2" />
                {t('add_lens')}
              </Button>
            </DialogTrigger>
            <DialogContent className={isMobile ? "w-[95vw] max-w-[95vw] p-4" : "max-w-md"}>
              <DialogHeader className="mb-4">
                <DialogTitle>{t('add_new_lens')}</DialogTitle>
                <DialogDescription>
                  {t('add_lens_details')}
                </DialogDescription>
              </DialogHeader>
              <LensTabsForm form={form} onSubmit={onSubmit} />
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
